/**
 * Operations Command Centre · Tile Target Registry (ISSUE-003 · Slice 2 · rev 2026-08-09)
 *
 * PRB governance rule OCC-1:
 *   Every actionable insight must have an actionable path — one that lands
 *   the operator DIRECTLY on the work queue that needs attention, never on
 *   a workspace landing page. See docs/governance/process/OCC-DEEP-LINKING-CONTRACT.md.
 *
 * ---
 *
 * Envelope:
 *   {
 *     [tile_key]: {
 *       route:        // top-level app route (matches App.js strict-eq route)
 *       queue:        // machine key identifying the target queue within that route
 *       queue_label:  // human label shown in the "Back to OCC" context banner
 *       filter:       { <arbitrary query params> }
 *       focus:        // optional DOM anchor id the receiver can scrollIntoView
 *       quick_actions: [ { key, label, filter?, focus? } ]
 *     }
 *   }
 *
 * The composed hash always uses a **single top-level route** so App.js's
 * strict-equality dispatcher continues to match. Every extra parameter
 * (queue, filter, focus, back-link marker) travels in the query string:
 *
 *   #<route>?queue=<queue>&<filter…>&focus=<anchor>&_from=occ
 *
 * The `_from=occ` marker triggers the global <OccContextHeader> ("Back to
 * Operations Command Centre") so the operator always knows how they
 * arrived and where "Back" leads.
 */

const REGISTRY = Object.freeze({
  // -----------------------------------------------------------------
  // Editorial / Knowledge workflow
  // -----------------------------------------------------------------
  media_requiring_approval: {
    // OCC counts canonical media_assets, not the separate hero_media store
    // used by Editorial Studio. Review them in their owning CKB subject.
    route: "ckb-studio",
    queue: "media_review",
    queue_label: "Media Review Queue",
    filter: { tab: "subjects", state: "draft,needs_review" },
    focus: "media-workspace",
    quick_actions: [
      { key: "review_queue", label: "Review queue", focus: "media-workspace" },
      { key: "approve_next", label: "Review next", focus: "media-workspace", filter: { cursor: "first" } },
    ],
  },
  policies_requiring_review: {
    route: "ckb-studio",
    queue: "policy_review",
    queue_label: "Policy Review Queue",
    // Policies are knowledge fields attached to subjects, not a subject type.
    // Match both states counted by routes_ops.knowledge_ops_producer.
    filter: { tab: "subjects", state: "draft,in_review" },
    quick_actions: [
      { key: "open_queue",  label: "Open queue" },
    ],
  },
  ckb_subjects_needing_review: {
    route: "ckb-studio",
    queue: "subject_review",
    queue_label: "Subject Review Queue",
    filter: { tab: "subjects", state: "in_review" },
    quick_actions: [
      { key: "open_queue",  label: "Open queue" },
    ],
  },
  broken_trusted_sources: {
    route: "ckb-studio",
    queue: "broken_sources",
    queue_label: "Broken Trusted Sources",
    filter: { tab: "subjects", source_health: "broken" },
    quick_actions: [
      { key: "open_queue", label: "Open queue" },
    ],
  },

  // -----------------------------------------------------------------
  // Booking operations
  // -----------------------------------------------------------------
  deposits_due: {
    route: "booking",
    queue: "deposits_due",
    queue_label: "Deposits Due",
    filter: { deposit_status: "due" },
    quick_actions: [
      { key: "open_queue",  label: "Open queue" },
      { key: "collect_now", label: "Collect deposit" },
    ],
  },
  awaiting_confirmation: {
    route: "booking",
    queue: "awaiting_confirmation",
    queue_label: "Awaiting Confirmation",
    filter: { lifecycle_state: "awaiting_confirmation" },
    quick_actions: [
      { key: "open_queue",   label: "Open queue" },
      { key: "confirm_next", label: "Confirm next" },
    ],
  },
  red_health_bookings: {
    route: "booking",
    queue: "red_health",
    queue_label: "Red-Health Bookings",
    filter: { health: "red" },
    quick_actions: [
      { key: "open_queue", label: "Open queue" },
    ],
  },

  // -----------------------------------------------------------------
  // Traveler operations
  // -----------------------------------------------------------------
  guests_not_checked_in: {
    route: "travelers",
    queue: "checkin_pending",
    queue_label: "Check-In Pending",
    filter: { checkin_status: "pending" },
    quick_actions: [
      { key: "open_roster", label: "Open roster" },
    ],
  },
  passports_expiring_soon: {
    route: "travelers",
    queue: "passports_expiring",
    queue_label: "Passports Expiring Soon",
    filter: { passport_status: "expiring" },
    focus: "passport",
    quick_actions: [
      { key: "open_roster",   label: "Open roster", focus: "passport" },
      { key: "contact_next",  label: "Contact next" },
    ],
  },

  // -----------------------------------------------------------------
  // Financial operations · target = Finances workspace with queue banner.
  // Full deep-filter deferred until FinancesWorkspace supports queue tabs
  // (tracked in docs/governance/process/OCC-DEEP-LINKING-CONTRACT.md §Backlog).
  // -----------------------------------------------------------------
  outstanding_balances: {
    route: "finances",
    queue: "outstanding_balances",
    queue_label: "Outstanding Balances",
    filter: { status: "outstanding" },
    quick_actions: [
      { key: "open_queue", label: "Open queue" },
    ],
  },
  refunds_required: {
    route: "finances",
    queue: "refunds_required",
    queue_label: "Refunds Required",
    filter: {},
    quick_actions: [
      { key: "open_queue",   label: "Open queue" },
      { key: "process_next", label: "Process next" },
    ],
  },

  // -----------------------------------------------------------------
  // Cruise operations · target = Cruise portfolio (Voyages) with banner.
  // -----------------------------------------------------------------
  boarding_window_open: {
    route: "voyages",
    queue: "boarding_window",
    queue_label: "Boarding Window Open",
    filter: { phase: "boarding" },
  },
  active_disruptions: {
    route: "voyages",
    queue: "active_disruptions",
    queue_label: "Active Disruptions",
    filter: {},
    quick_actions: [
      { key: "open_queue",   label: "Open queue" },
      { key: "acknowledge",  label: "Acknowledge next" },
    ],
  },
  voyages_in_port: {
    route: "voyages",
    queue: "voyages_in_port",
    queue_label: "Voyages In Port",
    filter: { phase: "in_port" },
  },
  upcoming_sailings: {
    route: "voyages",
    queue: "upcoming_sailings",
    queue_label: "Upcoming Sailings",
    filter: { phase: "upcoming" },
  },
});

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

const encodeParams = (obj) => {
  const entries = Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (!entries.length) return "";
  return "?" + new URLSearchParams(entries).toString();
};

/** Look up a tile_key. Returns the entry or null (unregistered tile). */
export const lookupTile = (tileKey) => REGISTRY[tileKey] || null;

/**
 * Compose the destination hash for a tile and optional Quick Action.
 * Always emits a SINGLE top-level route so App.js's strict-eq dispatcher
 * matches; every filter / queue / focus / _from marker rides the query.
 *
 *   href("media_requiring_approval")
 *     → "#editorial-studio?queue=media_review&state=needs_review&focus=editorial-needs-attention&_from=occ"
 */
export const href = (tileKey, quickActionKey = null) => {
  const entry = lookupTile(tileKey);
  if (!entry) return null;
  let filter = { ...(entry.filter || {}) };
  let focus = entry.focus || null;
  if (quickActionKey) {
    const qa = (entry.quick_actions || []).find((a) => a.key === quickActionKey);
    if (qa) {
      if (qa.filter) filter = { ...filter, ...qa.filter };
      if (qa.focus) focus = qa.focus;
    }
  }
  const params = {
    queue: entry.queue,
    ...filter,
    ...(focus ? { focus } : {}),
    _from: "occ",
  };
  return `#${entry.route}${encodeParams(params)}`;
};

/**
 * Convenience for React consumers. Returns { href, quick_actions[] }.
 */
export const resolveTile = (tileKey) => {
  const entry = lookupTile(tileKey);
  if (!entry) return null;
  return {
    href: href(tileKey),
    queue_label: entry.queue_label,
    quick_actions: (entry.quick_actions || []).map((qa) => ({
      ...qa,
      href: href(tileKey, qa.key),
    })),
  };
};

export default REGISTRY;
